import {
  Context,
  Dispatch,
  memo,
  MemoExoticComponent,
  Provider,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';

import styled from 'styled-components';
import {Form, Field, FieldMetaState} from 'react-final-form';

import FormField from '~/shared/components/FormField';
import useFormAutoFocus from '~/shared/hooks/useFormAutoFocus';
import {
  FieldWrapper,
  FormWrapper,
  HeaderText,
  LoginFormFieldsWrapper,
  Email,
  BlackLinkButton,
  LinkButtonsWrapper,
} from '~/shared/components/loginStyled';
import useCanResendCode from '~/shared/utils/useCanResendCode';
import {getLocalizationService} from '~/shared/services/localisationService';
import {FlexCenter, FlexColumn} from '~/shared/theme/FlexLayout';
import {bodyBold, bodyRegular} from '~/shared/theme/typography';
import VerificationCodeInput from '~/shared/components/VerificationCodeInput';

import {VerificationFormContext, VerificationFormContextProps} from './context';
import GreenCheckCircleIcon from './greenCheckCircle.svg?react';

const StyledFieldWrapper = styled(FieldWrapper)`
  margin-top: 32px;
`;

const StyledLinkButtonsWrapper = styled(LinkButtonsWrapper)`
  margin-top: 8px;
  gap: 10px;
`;

const getVerificationInputError = (resErrorDescription: string | null, meta: FieldMetaState<string>): string | undefined => {
  if (meta.dirtySinceLastSubmit) {
    return;
  }

  if (meta.submitFailed) {
    return meta.error;
  }

  return resErrorDescription || undefined;
};

const codeValidation = (value: string) => {
  if (!value || value.length !== 5) {
    return 'activation_code_must_be_exact_5_digits';
  }

  return undefined;
};

const HeaderTexts = styled(FlexColumn)`
  gap: 8px;
  margin-bottom: 8px;

  .verification-form-header {
    ${bodyRegular}
  }
`;

const ResendCodeWrapper = styled(FlexCenter)`
  width: 100%;
  gap: 4px;
`;

const ReSendCodeIndication = styled.label.attrs({
  role: 'alert',
})`
  ${bodyBold}
  color: ${({theme}) => theme.colors.contentPositive};
  text-align: center;
`;

interface IVerificationForm {
  onReSendCode: () => void;
  onSubmit: ({authenticationCode}: {authenticationCode?: string}) => void;
  email?: string;
  headerText: ReactNode;
  resErrors: string | null;
  setResErrors: Dispatch<SetStateAction<string | null>>;
  toUpdatePhoneNumberScreen?: () => void;
  isGov?: boolean;
  className?: string;
}

const VerificationForm = ({
  onReSendCode,
  onSubmit,
  email,
  headerText,
  resErrors: resErrorFromServer,
  setResErrors,
  toUpdatePhoneNumberScreen,
  isGov,
  className,
}: IVerificationForm) => {
  const {t} = getLocalizationService();
  const [isTriedReSendingCode, setIsTriedReSendingCode] = useState<boolean | null>(false);
  const {isResendButtonDisabled, setIsResendButtonDisabled} = useCanResendCode();

  const resetCode = () => {
    if (isResendButtonDisabled) {
      return;
    }

    setIsTriedReSendingCode(true);
    onReSendCode();
    setIsResendButtonDisabled(true);
  };

  const {userError, isRequireCaptcha} = useContext(VerificationFormContext);
  const resErrorDescription = useMemo(() => userError?.message || resErrorFromServer, [resErrorFromServer, userError]);
  const formWrapRef = useFormAutoFocus();
  return (
    <div className={className}>
      <Form
        onSubmit={onSubmit}
        render={({handleSubmit}) => (
          <FormWrapper onSubmit={handleSubmit} ref={formWrapRef}>
            {!isGov && (
              <HeaderTexts>
                <Email className="verification-form-email">{email}</Email>
                <HeaderText className="verification-form-header">{headerText}</HeaderText>
              </HeaderTexts>
            )}
            <LoginFormFieldsWrapper>
              <StyledFieldWrapper isGov={isGov}>
                <Field
                  name="authenticationCode"
                  validate={codeValidation}
                  render={({meta, input}) => (
                    <VerificationCodeInput
                      onChange={code => {
                        input.onChange(code);
                        if (!resErrorDescription) {
                          return;
                        }
                        setIsTriedReSendingCode(null);
                        setResErrors(null);
                      }}
                      onComplete={() => handleSubmit()}
                      error={getVerificationInputError(resErrorDescription, meta)}
                    />
                  )}
                />
              </StyledFieldWrapper>
              {/* @ts-expect-error TODO: refactor FormField to TS*/}
              {isRequireCaptcha && <FormField name="reCaptchaResponse" type="recaptcha" />}
            </LoginFormFieldsWrapper>
          </FormWrapper>
        )}
      />
      <StyledLinkButtonsWrapper>
        {toUpdatePhoneNumberScreen && (
          <BlackLinkButton isGov={isGov} onClick={toUpdatePhoneNumberScreen}>{t('update_phone_number')}</BlackLinkButton>
        )}
        <BlackLinkButton className="resend-code-button" isGov={isGov} onClick={resetCode} isDisabled={isResendButtonDisabled}>
          {t('i_did_not_receive_send_again')}
        </BlackLinkButton>
        {isResendButtonDisabled && isTriedReSendingCode && (
          <ResendCodeWrapper>
            <GreenCheckCircleIcon />
            <ReSendCodeIndication>
              {t('we_sent_you_a_new_code')}
            </ReSendCodeIndication>
          </ResendCodeWrapper>
        )}
      </StyledLinkButtonsWrapper>
    </div>
  );
};

const PureVerificationForm = memo(VerificationForm) as MemoExoticComponent<
  (props: IVerificationForm) => JSX.Element | null
> & {
  Provider: Provider<VerificationFormContextProps>;
  Context: Context<VerificationFormContextProps>;
};

PureVerificationForm.Context = VerificationFormContext;
PureVerificationForm.Provider = VerificationFormContext.Provider;

export default PureVerificationForm;
